import { memo, useMemo } from 'react';
import { format, parseISO, isValid } from 'date-fns';
import {
  MANDATORY_COLUMNS,
  OPTIONAL_COLUMNS,
  getFormattedLabels,
  mapGridDetailToRow,
  generateCalendarWeekGroups,
  generateMonthGroups,
  formatWeekHeader,
  formatMonthHeader,
} from './reportTableStyles';
import MultiCorpWeeklyTable from './MultiCorpWeeklyTable';
import MultiCorpMonthlyTable from './MultiCorpMonthlyTable';
import MultiCorpDailyTable from './MultiCorpDailyTable';
import SingleCorpTable from './SingleCorpTable';

/**
 * Time and Attendance Report Tables Coordinator
 * Dispatches to specialized modular table views:
 * - MultiCorpWeeklyTable: Multi-Corp combined weekly matrix
 * - MultiCorpMonthlyTable: Multi-Corp combined monthly matrix
 * - MultiCorpDailyTable: Multi-Corp combined daily matrix
 * - SingleCorpTable: Single corporation view (Daily, Weekly, Monthly) with employee details
 */
const Tables = ({
  data,
  reportData,
  selectedFields = ['overtimeHrs', 'varianceHrs', 'status'],
  reportType = 'daily',
  filters,
}) => {
  const rawGridDetails = useMemo(() => {
    if (reportData?.GridDetails && Array.isArray(reportData.GridDetails)) {
      return reportData.GridDetails;
    }
    if (data?.GridDetails && Array.isArray(data.GridDetails)) {
      return data.GridDetails;
    }
    if (Array.isArray(reportData)) {
      return reportData;
    }
    if (Array.isArray(data)) {
      return data;
    }
    return [];
  }, [reportData, data]);

  const isMultiCorp = useMemo(() => {
    if (filters?.selectedCorporationIds && filters.selectedCorporationIds.length > 1) return true;
    if (filters?.corporation && filters.corporation.includes('Corporations Selected')) {
      const count = parseInt(filters.corporation, 10);
      if (!isNaN(count) && count > 1) return true;
    }
    if (filters?.corporation === 'all' || filters?.corporation === 'Multiple') return true;
    if (filters?.corporations && filters.corporations.length > 1) return true;
    if (rawGridDetails && rawGridDetails.length > 0) {
      const corps = new Set(rawGridDetails.map((r) => r.CorporationID || r.CorporationName).filter(Boolean));
      if (corps.size > 1) return true;
    }
    return false;
  }, [filters, rawGridDetails]);

  const isDaily = reportType === 'daily';
  const { deptLabel, jobLabel } = useMemo(() => getFormattedLabels(filters), [filters]);

  const mappedApiRecords = useMemo(() => {
    if (rawGridDetails && rawGridDetails.length > 0) {
      return rawGridDetails.map(mapGridDetailToRow);
    }
    return [];
  }, [rawGridDetails]);

  const hasEmployeeDetails = useMemo(() => {
    if (!rawGridDetails || rawGridDetails.length === 0) return true;
    return rawGridDetails.some((r) => Boolean(r.EmployeeName || r.EmployeeId));
  }, [rawGridDetails]);

  const hasProfitCenter = useMemo(() => {
    if (!rawGridDetails || rawGridDetails.length === 0) return true;
    return rawGridDetails.some((item) => {
      const pcId = item.PCID ?? item.pcId ?? item.ProfitCenterID;
      const pcName = item.PCName ?? item.pcName ?? item.ProfitCenterName;
      const validId =
        pcId !== null &&
        pcId !== undefined &&
        String(pcId).trim() !== '' &&
        String(pcId).toLowerCase() !== 'null' &&
        String(pcId).toLowerCase() !== 'undefined';
      const validName =
        pcName !== null &&
        pcName !== undefined &&
        String(pcName).trim() !== '' &&
        String(pcName).toLowerCase() !== 'null' &&
        String(pcName).toLowerCase() !== 'undefined';
      return validId || validName;
    });
  }, [rawGridDetails]);

  const hasDepartment = useMemo(() => {
    if (!rawGridDetails || rawGridDetails.length === 0) return false;
    return rawGridDetails.some((item) => {
      const deptName = item.NimbleDeptName ?? item.DepartmentName ?? item.deptName ?? item.Department;
      return (
        deptName !== null &&
        deptName !== undefined &&
        String(deptName).trim() !== '' &&
        String(deptName).toLowerCase() !== 'null' &&
        String(deptName).toLowerCase() !== 'undefined'
      );
    });
  }, [rawGridDetails]);

  const hasJob = useMemo(() => {
    if (!rawGridDetails || rawGridDetails.length === 0) return false;
    return rawGridDetails.some((item) => {
      const jobName = item.NimbleJobName ?? item.JobName ?? item.jobName ?? item.JobTitle ?? item.Job;
      return (
        jobName !== null &&
        jobName !== undefined &&
        String(jobName).trim() !== '' &&
        String(jobName).toLowerCase() !== 'null' &&
        String(jobName).toLowerCase() !== 'undefined'
      );
    });
  }, [rawGridDetails]);

  const allowedOptionalColumns = useMemo(() => {
    if (isDaily) {
      if (isMultiCorp) {
        return OPTIONAL_COLUMNS.filter((col) => col.id !== 'status' && col.id !== 'overtimeHrs' && col.id !== 'varianceHrs');
      }
      return OPTIONAL_COLUMNS.filter((col) => col.id !== 'otPercent' && col.id !== 'otRisk' && col.id !== 'varianceOvertimeHrs');
    }
    return OPTIONAL_COLUMNS.filter((col) => col.id !== 'status' && col.id !== 'punchIn' && col.id !== 'punchOut' && col.id !== 'varianceOvertimeHrs');
  }, [isDaily, isMultiCorp]);

  const activeFields = useMemo(() => {
    let fields = Array.isArray(selectedFields) && selectedFields.length > 0
      ? [...selectedFields]
      : (!isDaily
        ? ['scheduledHrs', 'actualHrs', 'overtimeHrs', 'varianceHrs', 'otPercent', 'otRisk']
        : (isMultiCorp
          ? ['punchIn', 'punchOut', 'scheduledHrs', 'actualHrs', 'varianceOvertimeHrs', 'otPercent', 'otRisk']
          : ['punchIn', 'punchOut', 'scheduledHrs', 'actualHrs', 'overtimeHrs', 'varianceHrs', 'status']));

    if (!isDaily || !isMultiCorp) {
      if (fields.includes('varianceOvertimeHrs')) {
        fields = fields.filter((f) => f !== 'varianceOvertimeHrs');
        if (!fields.includes('overtimeHrs')) fields.push('overtimeHrs');
        if (!fields.includes('varianceHrs')) fields.push('varianceHrs');
      }
    }
    return fields;
  }, [selectedFields, isDaily, isMultiCorp]);

  const activeOptionalColumns = useMemo(() => {
    return allowedOptionalColumns.filter((col) => activeFields.includes(col.id));
  }, [allowedOptionalColumns, activeFields]);

  // Determine base columns
  const baseColumns = useMemo(() => {
    if (isDaily) {
      if (isMultiCorp) {
        const cols = [{ id: 'corporation', label: 'Corporation', align: 'left', minWidth: 140 }];
        if (hasProfitCenter) {
          cols.push({ id: 'profitCenter', label: 'Profit Center', align: 'left', minWidth: 120 });
        }
        cols.push(
          { id: 'employeeName', label: 'Employee Name', align: 'left', minWidth: 160 },
          { id: 'department', label: 'Department', align: 'left', minWidth: 140 },
          { id: 'jobTitle', label: 'Job Title', align: 'left', minWidth: 140 }
        );
        return cols;
      }
      return MANDATORY_COLUMNS;
    }
    if (reportType === 'weekly') {
      const cols = [{ id: 'employeeName', label: 'Employee Name', align: 'left', minWidth: 160 }];
      if (hasProfitCenter && isMultiCorp) {
        cols.push({ id: 'profitCenter', label: 'Profit Center', align: 'left', minWidth: 120 });
      }
      if (isMultiCorp) {
        cols.push(
          { id: 'department', label: 'Department', align: 'left', minWidth: 140 },
          { id: 'jobTitle', label: 'Job Title', align: 'left', minWidth: 140 }
        );
      }
      if (!hasEmployeeDetails) {
        return cols.map((col) =>
          col.id === 'employeeName' ? { ...col, label: 'Employee count' } : col
        );
      }
      return cols;
    }
    const cols = MANDATORY_COLUMNS.filter((col) => col.id !== 'date');
    if (!hasEmployeeDetails) {
      return cols.map((col) =>
        col.id === 'employeeName' ? { ...col, label: 'Employee count' } : col
      );
    }
    return cols;
  }, [isDaily, isMultiCorp, reportType, hasProfitCenter, hasEmployeeDetails]);

  const visibleColumns = useMemo(() => {
    return [...baseColumns, ...activeOptionalColumns];
  }, [baseColumns, activeOptionalColumns]);

  const footerColSpan = useMemo(() => {
    let span = baseColumns.length;
    if (isDaily) {
      if (activeOptionalColumns.some((c) => c.id === 'punchIn')) span += 1;
      if (activeOptionalColumns.some((c) => c.id === 'punchOut')) span += 1;
    }
    return span;
  }, [baseColumns.length, isDaily, activeOptionalColumns]);

  // Single & Multi-Corporation Employee Groups
  const tableGroups = useMemo(() => {
    if (isMultiCorp && !hasEmployeeDetails && isDaily) return [];

    const fromDate = filters?.fromDate || '2026-01-01';
    const toDate = filters?.toDate || '2026-01-31';

    if (reportType === 'weekly') {
      if (mappedApiRecords.length > 0) {
        const hasExplicitWeeks = mappedApiRecords.some((r) => r.week || r.weekStartDate);

        if (hasExplicitWeeks) {
          const weeklyMap = new Map();
          mappedApiRecords.forEach((rec) => {
            const corpPrefix = isMultiCorp ? `${rec.corporationName || ''}___` : '';
            const weekKey = `${corpPrefix}${rec.week || 'W1'}___${rec.weekStartDate || ''}___${rec.weekEndDate || ''}`;
            if (!weeklyMap.has(weekKey)) {
              weeklyMap.set(weekKey, {
                corpName: rec.corporationName,
                weekCode: rec.week || 'W1',
                weekStartDate: rec.weekStartDate,
                weekEndDate: rec.weekEndDate,
                records: [],
              });
            }
            weeklyMap.get(weekKey).records.push(rec);
          });

          const weeklyGroups = [];
          weeklyMap.forEach((val, key) => {
            const rawWeekTitle = formatWeekHeader(val.weekCode, val.weekStartDate, val.weekEndDate);
            const weekTitle = isMultiCorp && val.corpName ? `${val.corpName} | ${rawWeekTitle}` : rawWeekTitle;
            const corpTitle = val.corpName ? `${val.corpName} | ` : '';
            const headerTitle = !isMultiCorp
              ? rawWeekTitle
              : `${corpTitle}${rawWeekTitle}`;

            weeklyGroups.push({
              id: `weekly-group-${key}`,
              headerTitle,
              weekTitle,
              corpTitle: val.corpName || '',
              records: val.records,
            });
          });
          return weeklyGroups;
        }

        return generateCalendarWeekGroups(
          fromDate,
          toDate,
          '',
          '',
          mappedApiRecords
        );
      }
      return generateCalendarWeekGroups(fromDate, toDate, '', '', data);
    }

    if (reportType === 'monthly') {
      if (mappedApiRecords.length > 0) {
        const hasExplicitMonths = mappedApiRecords.some((r) => r.month || r.monthStartDate);

        if (hasExplicitMonths) {
          const monthMap = new Map();
          mappedApiRecords.forEach((rec) => {
            const corpPrefix = isMultiCorp ? `${rec.corporationName || ''}___` : '';
            const monthKey = `${corpPrefix}${rec.month || 'M1'}___${rec.year || ''}___${rec.monthStartDate || ''}___${rec.monthEndDate || ''}`;
            if (!monthMap.has(monthKey)) {
              monthMap.set(monthKey, {
                corpName: rec.corporationName,
                monthCode: rec.month || 'M1',
                year: rec.year || '',
                monthStartDate: rec.monthStartDate,
                monthEndDate: rec.monthEndDate,
                records: [],
              });
            }
            monthMap.get(monthKey).records.push(rec);
          });

          const monthGroups = [];
          monthMap.forEach((val, key) => {
            const rawMonthTitle = formatMonthHeader(val.monthCode, val.monthStartDate, val.year);
            const monthTitle = isMultiCorp && val.corpName ? `${val.corpName} | ${rawMonthTitle}` : rawMonthTitle;
            const corpTitle = val.corpName ? `${val.corpName} | ` : '';
            const headerTitle = !isMultiCorp
              ? rawMonthTitle
              : `${corpTitle}${rawMonthTitle}`;

            monthGroups.push({
              id: `month-group-${key}`,
              headerTitle,
              monthTitle,
              corpTitle: val.corpName || '',
              records: val.records,
            });
          });
          return monthGroups;
        }

        return generateMonthGroups(fromDate, toDate, deptLabel, jobLabel, mappedApiRecords, !isMultiCorp);
      }
      return generateMonthGroups(fromDate, toDate, deptLabel, jobLabel, data, !isMultiCorp);
    }

    // Default Daily Mode
    if (mappedApiRecords.length > 0) {
      if (!isMultiCorp) {
        const dailyMap = new Map();
        mappedApiRecords.forEach((rec) => {
          const key = `${rec.department}___${rec.jobTitle}`;
          if (!dailyMap.has(key)) {
            dailyMap.set(key, {
              date: rec.date || filters?.fromDate || '',
              deptName: rec.department || 'All',
              jobName: rec.jobTitle || 'All',
              records: [],
            });
          }
          dailyMap.get(key).records.push(rec);
        });

        const dailyGroups = [];
        dailyMap.forEach((val, key) => {
          dailyGroups.push({
            id: `daily-group-${key}`,
            headerTitle: `${val.deptName} / ${val.jobName}`,
            records: val.records,
          });
        });
        return dailyGroups;
      }

      // Multi-Corporation Daily Mode: 1st complete 1 corporation, then start another corporation
      const corpMap = new Map();
      mappedApiRecords.forEach((rec) => {
        const corpKey = rec.corporationName || rec.CorporationID || 'Corporation';
        if (!corpMap.has(corpKey)) {
          corpMap.set(corpKey, new Map());
        }
        const dateKey = rec.date || filters?.fromDate || '';
        const dateMap = corpMap.get(corpKey);
        if (!dateMap.has(dateKey)) {
          dateMap.set(dateKey, {
            corpName: rec.corporationName || '',
            date: dateKey,
            records: [],
          });
        }
        dateMap.get(dateKey).records.push(rec);
      });

      const dailyGroups = [];
      corpMap.forEach((dateMap, corpKey) => {
        dateMap.forEach((val, dateKey) => {
          let formattedDate = val.date;
          if (val.date) {
            try {
              formattedDate = isValid(parseISO(val.date))
                ? format(parseISO(val.date), 'MM-dd-yyyy')
                : val.date;
            } catch {
              formattedDate = val.date;
            }
          }

          dailyGroups.push({
            id: `daily-group-${corpKey}-${dateKey}`,
            headerTitle: formattedDate,
            dateTitle: formattedDate,
            corpTitle: val.corpName || '',
            records: val.records,
          });
        });
      });
      return dailyGroups;
    }

    if (data && data.length > 0) {
      return [
        {
          id: 'daily-group',
          headerTitle: `${deptLabel} / ${jobLabel}`,
          records: data,
        },
      ];
    }

    return [];
  }, [isMultiCorp, hasEmployeeDetails, reportType, isDaily, deptLabel, jobLabel, data, mappedApiRecords, filters?.fromDate, filters?.toDate]);

  // Helper to calculate totals for single corporation records
  const calculateTotals = (records = []) => {
    return records.reduce(
      (acc, row) => {
        acc.scheduledHrs += Number(row.scheduledHrs) || 0;
        acc.actualHrs += Number(row.actualHrs) || 0;
        acc.overtimeHrs += Number(row.overtimeHrs) || 0;
        acc.varianceHrs += Number(row.varianceHrs) || 0;
        return acc;
      },
      { scheduledHrs: 0, actualHrs: 0, overtimeHrs: 0, varianceHrs: 0 }
    );
  };

  const allRecords = useMemo(() => {
    return tableGroups.flatMap((group) => group.records);
  }, [tableGroups]);

  const grandTotals = useMemo(() => {
    return calculateTotals(allRecords);
  }, [allRecords]);

  // =========================================================================
  // VIEW DISPATCHING
  // =========================================================================

  // Multi-Corp 1: Weekly Combined View
  if (isMultiCorp && !hasEmployeeDetails && reportType === 'weekly') {
    return (
      <MultiCorpWeeklyTable
        rawGridDetails={rawGridDetails}
        hasProfitCenter={hasProfitCenter}
        hasDepartment={hasDepartment}
        hasJob={hasJob}
        activeFields={activeFields}
      />
    );
  }

  // Multi-Corp 2: Monthly Combined View
  if (isMultiCorp && !hasEmployeeDetails && reportType === 'monthly') {
    return (
      <MultiCorpMonthlyTable
        rawGridDetails={rawGridDetails}
        hasProfitCenter={hasProfitCenter}
        hasDepartment={hasDepartment}
        hasJob={hasJob}
        activeFields={activeFields}
      />
    );
  }

  // Multi-Corp 3: Daily Combined View
  if (isMultiCorp && !hasEmployeeDetails && isDaily) {
    return (
      <MultiCorpDailyTable
        rawGridDetails={rawGridDetails}
        hasProfitCenter={hasProfitCenter}
        activeFields={activeFields}
      />
    );
  }

  // Default Fallthrough: Single Corporation View (Daily / Weekly / Monthly with full employee details)
  return (
    <SingleCorpTable
      visibleColumns={visibleColumns}
      tableGroups={tableGroups}
      calculateTotals={calculateTotals}
      isMultiCorp={isMultiCorp}
      isDaily={isDaily}
      reportType={reportType}
      hasProfitCenter={hasProfitCenter}
      activeOptionalColumns={activeOptionalColumns}
      footerColSpan={footerColSpan}
      allRecords={allRecords}
      grandTotals={grandTotals}
    />
  );
};

export default memo(Tables);
